// src/pages/EventDetailPage.js
// Shareable event detail page at /site/:slug/events/:eventSlug
// Public, no auth required. Registration requires auth.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { db, auth } from '../config/firebase-config';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { sanitizeHtml } from '../utils/sanitize';
import {
  registerForEvent, confirmEventRegistration, checkRegistrationStatus, cancelEventRegistration,
} from '../services/event-service';
import { Calendar, Clock, Monitor, MapPin, Users, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import { trackEvent } from '../utils/analytics';

const SITE_BASE_URL = 'https://divineframeworks.com';
const DEFAULT_OG_IMAGE = 'https://divineframeworks.com/og-image.png';

// ─── Helpers ────────────────────────────────────────────────────────────────

function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
}

function formatTime12(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function getTimezoneLabel(tz) {
  if (!tz) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'long' }).formatToParts(new Date());
    return parts.find(p => p.type === 'timeZoneName')?.value || tz;
  } catch { return tz; }
}

function getTimezoneAbbr(tz) {
  if (!tz) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date());
    return parts.find(p => p.type === 'timeZoneName')?.value || '';
  } catch { return ''; }
}

function formatDateLong(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function formatDateShort(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

function isEventPast(event) {
  const { eventType, schedule } = event;
  const today = new Date().toISOString().split('T')[0];
  if (eventType === 'single' && schedule?.date) return schedule.date < today;
  if (eventType === 'series' && schedule?.sessionDates?.length > 0) {
    return schedule.sessionDates[schedule.sessionDates.length - 1] < today;
  }
  if (eventType === 'multi_day' && schedule?.endDate) return schedule.endDate < today;
  return false;
}

function parseTimeToMinutes(time24) {
  if (!time24) return 0;
  const [h, m] = time24.split(':').map(Number);
  return h * 60 + m;
}

function minutesToTime(mins) {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

// ─── Sub-components ─────────────────────────────────────────────────────────

const InfoCard = ({ title, children }) => (
  <div style={{
    background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 12,
    padding: '20px 24px', marginBottom: 20,
  }}>
    {title && (
      <h3 style={{
        fontSize: 14, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase',
        letterSpacing: '0.05em', marginBottom: 16, fontFamily: 'Montserrat, sans-serif',
      }}>
        {title}
      </h3>
    )}
    {children}
  </div>
);

const DetailRow = ({ icon: Icon, children }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
    <div style={{ marginTop: 2, flexShrink: 0 }}><Icon size={16} color="#6B7280" /></div>
    <div style={{ fontSize: 15, color: '#374151', fontFamily: 'Montserrat, sans-serif', lineHeight: 1.5 }}>{children}</div>
  </div>
);

const Banner = ({ color, bgColor, children }) => (
  <div style={{
    background: bgColor, padding: '12px 20px', textAlign: 'center',
    fontSize: 15, fontWeight: 600, color, fontFamily: 'Montserrat, sans-serif',
    borderBottom: `1px solid ${color}22`,
  }}>
    {children}
  </div>
);

// ─── Registration Success Card ──────────────────────────────────────────────

const RegistrationSuccess = ({ event, registration, practitionerSlug }) => {
  const eventDate = event.eventType === 'single' && event.schedule?.date
    ? `${formatDateLong(event.schedule.date)} • ${formatTime12(event.schedule.startTime)}`
    : event.eventType === 'series' && event.schedule?.sessionDates?.[0]
    ? `Starting ${formatDateLong(event.schedule.sessionDates[0])}`
    : event.eventType === 'multi_day' && event.schedule?.date
    ? `${formatDateLong(event.schedule.date)} — ${formatDateLong(event.schedule.endDate)}`
    : '';

  return (
    <div style={{
      background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 12,
      padding: '24px', marginBottom: 24, textAlign: 'center',
    }}>
      <CheckCircle size={36} color="#16A34A" style={{ margin: '0 auto 12px' }} />
      <h3 style={{ fontSize: 20, fontWeight: 700, color: '#15803D', margin: '0 0 8px', fontFamily: 'Montserrat, sans-serif' }}>
        You're registered!
      </h3>
      <p style={{ fontSize: 16, fontWeight: 600, color: '#111827', margin: '0 0 4px', fontFamily: 'Montserrat, sans-serif' }}>
        {event.title}
      </p>
      <p style={{ fontSize: 14, color: '#6B7280', margin: '0 0 16px', fontFamily: 'Montserrat, sans-serif' }}>
        {eventDate}
      </p>

      {/* Zoom link — visible to registered attendees */}
      {(event.format === 'virtual' || event.format === 'hybrid') && (event.zoomJoinUrl || event.manualMeetingLink) && (
        <div style={{
          background: '#FFFFFF', borderRadius: 8, padding: '12px 16px', marginBottom: 16,
          border: '1px solid #E5E7EB', textAlign: 'left',
        }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: '#6B7280', margin: '0 0 4px', fontFamily: 'Montserrat, sans-serif' }}>
            Your meeting link:
          </p>
          <a
            href={event.zoomJoinUrl || event.manualMeetingLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 14, color: '#7c3aed', fontWeight: 600, wordBreak: 'break-all', fontFamily: 'Montserrat, sans-serif' }}
          >
            {event.zoomJoinUrl || event.manualMeetingLink}
          </a>
        </div>
      )}

      {/* Deposit info */}
      {event.pricingModel === 'deposit' && registration?.paymentStatus === 'deposit_paid' && (
        <div style={{
          background: '#FFFFFF', borderRadius: 8, padding: '12px 16px', marginBottom: 16,
          border: '1px solid #FDE68A', textAlign: 'left',
        }}>
          <p style={{ fontSize: 14, color: '#92400E', fontFamily: 'Montserrat, sans-serif', margin: 0 }}>
            Deposit paid: ${((event.pricing?.depositAmount || 0) / 100).toFixed(2)}
          </p>
          <p style={{ fontSize: 13, color: '#92400E', fontFamily: 'Montserrat, sans-serif', margin: '4px 0 0' }}>
            Remaining balance of ${(((event.pricing?.totalPrice || 0) - (event.pricing?.depositAmount || 0)) / 100).toFixed(2)} due {event.pricing?.remainderDueDays || 7} days before the event
          </p>
        </div>
      )}

      <p style={{ fontSize: 13, color: '#6B7280', fontFamily: 'Montserrat, sans-serif', margin: 0 }}>
        A confirmation email has been sent to your email address.
      </p>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────

const EventDetailPage = () => {
  const { slug: practitionerSlug, eventSlug } = useParams();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [event, setEvent] = useState(null);
  const [practitionerId, setPractitionerId] = useState(null);
  const [eventId, setEventId] = useState(null);
  const [practitionerName, setPractitionerName] = useState('');
  const [practitionerPhoto, setPractitionerPhoto] = useState(null);
  const [practitionerBio, setPractitionerBio] = useState('');

  // Auth + registration state
  const [currentUser, setCurrentUser] = useState(undefined); // undefined = loading, null = not logged in
  const [registration, setRegistration] = useState(null); // existing registration doc
  const [registering, setRegistering] = useState(false);
  const [regError, setRegError] = useState('');
  const [justRegistered, setJustRegistered] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // ── Auth listener ──
  useEffect(() => {
    let done = false;
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user && !done) {
        done = true;
        setCurrentUser(user);
      } else if (!user) {
        // Wait briefly — Firebase fires null first
        setTimeout(() => { if (!done) { done = true; setCurrentUser(null); } }, 1500);
      }
    });
    return () => unsub();
  }, []);

  // ── Load event ──
  useEffect(() => {
    if (!practitionerSlug || !eventSlug || !db) { setStatus('not-found'); return; }

    (async () => {
      try {
        const slugKey = `${practitionerSlug}_${eventSlug}`;
        const slugDoc = await getDoc(doc(db, 'eventSlugs', slugKey));
        if (!slugDoc.exists()) { setStatus('not-found'); return; }

        const { practitionerId: pid, eventId: eid } = slugDoc.data();
        setPractitionerId(pid);
        setEventId(eid);

        const eventDoc = await getDoc(doc(db, 'practitioners', pid, 'events', eid));
        if (!eventDoc.exists()) { setStatus('not-found'); return; }

        const eventData = { id: eventDoc.id, ...eventDoc.data() };
        if (eventData.status === 'draft') { setStatus('not-found'); return; }

        setEvent(eventData);

        const practDoc = await getDoc(doc(db, 'practitioners', pid));
        if (practDoc.exists()) {
          const pData = practDoc.data();
          const pub = pData.website_published || {};
          setPractitionerName(pub.profile?.displayName || pub.profile?.name || pData.displayName || pData.name || 'Practitioner');
          setPractitionerPhoto(pub.profile?.photo || null);
          const mods = pub.profile?.modalities?.selected || [];
          if (mods.length > 0) {
            setPractitionerBio(mods.map(m => m.name || m.label || m).filter(Boolean).slice(0, 3).join(' · '));
          }
        }

        setStatus('ready');
        trackEvent('event_view', { event_type: eventData.eventType, practitioner_id: pid });
      } catch (err) {
        console.error('EventDetailPage load error:', err);
        setStatus('error');
      }
    })();
  }, [practitionerSlug, eventSlug]);

  // ── Check registration status when user + event are loaded ──
  useEffect(() => {
    if (!currentUser || !practitionerId || !eventId) return;

    (async () => {
      const reg = await checkRegistrationStatus(practitionerId, eventId, currentUser.uid);
      if (reg && reg.status === 'registered') {
        setRegistration(reg);
      }

      // Handle returning from Stripe
      const registered = searchParams.get('registered');
      const regId = searchParams.get('regId');
      if (registered === 'true' && regId && !reg) {
        try {
          await confirmEventRegistration(practitionerId, eventId, regId);
          const confirmed = await checkRegistrationStatus(practitionerId, eventId, currentUser.uid);
          if (confirmed) setRegistration(confirmed);
          setJustRegistered(true);
        } catch (err) {
          console.warn('Confirm registration error:', err);
          // Still check — might already be confirmed
          const rechecked = await checkRegistrationStatus(practitionerId, eventId, currentUser.uid);
          if (rechecked?.status === 'registered') {
            setRegistration(rechecked);
            setJustRegistered(true);
          }
        }
      }
    })();
  }, [currentUser, practitionerId, eventId, searchParams]);

  // ── Register handler ──
  const handleRegister = useCallback(async () => {
    setRegError('');

    // Not logged in — redirect to login
    if (!currentUser) {
      const returnUrl = `/site/${practitionerSlug}/events/${eventSlug}`;
      window.location.href = `/login?returnUrl=${encodeURIComponent(returnUrl)}`;
      return;
    }

    setRegistering(true);
    try {
      const result = await registerForEvent(practitionerId, eventId);

      if (result.paymentRequired && result.checkoutUrl) {
        // Redirect to Stripe
        window.location.href = result.checkoutUrl;
        return;
      }

      // Free event — registered immediately
      const reg = await checkRegistrationStatus(practitionerId, eventId, currentUser.uid);
      if (reg) setRegistration(reg);
      setJustRegistered(true);
      trackEvent('event_registration', { event_type: event?.eventType, pricing_model: event?.pricingModel, format: event?.format });
    } catch (err) {
      console.error('Registration error:', err);
      setRegError(err.message || 'Registration failed. Please try again.');
    } finally {
      setRegistering(false);
    }
  }, [currentUser, practitionerId, eventId, practitionerSlug, eventSlug]);

  // ── Cancel registration handler ──
  const handleCancelRegistration = useCallback(async () => {
    if (!registration || !window.confirm('Cancel your registration? Refunds follow the event policy.')) return;
    setCancelling(true);
    try {
      await cancelEventRegistration(practitionerId, eventId, registration.id);
      setRegistration(null);
      setJustRegistered(false);
    } catch (err) {
      console.error('Cancel registration error:', err);
      alert('Failed to cancel: ' + (err.message || 'Unknown error'));
    } finally {
      setCancelling(false);
    }
  }, [registration, practitionerId, eventId]);

  // ── Loading ──
  if (status === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F9FAFB' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 36, height: 36, border: '3px solid #E5E7EB', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          <p style={{ color: '#6B7280', fontSize: 14, fontFamily: 'Montserrat, sans-serif' }}>Loading event...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (status === 'not-found' || status === 'error') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F9FAFB' }}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', fontFamily: 'Montserrat, sans-serif', marginBottom: 8 }}>Event not found</h1>
          <p style={{ color: '#6B7280', fontSize: 15, fontFamily: 'Montserrat, sans-serif', marginBottom: 24 }}>This event may have been removed or the link is incorrect.</p>
          <a href={`/site/${practitionerSlug}`} style={{ color: '#7c3aed', fontWeight: 600, fontSize: 14, fontFamily: 'Montserrat, sans-serif', textDecoration: 'none' }}>← Back to website</a>
        </div>
      </div>
    );
  }

  // ── Ready ──
  const past = isEventPast(event);
  const cancelled = event.status === 'cancelled';
  const soldOut = event.currentAttendees >= event.maxAttendees;
  const spotsLeft = Math.max(0, (event.maxAttendees || 0) - (event.currentAttendees || 0));
  const registrationClosed = soldOut || (event.registrationDeadline && new Date() > new Date(event.registrationDeadline));
  const isRegistered = registration?.status === 'registered';

  // Pricing display
  const renderPricing = () => {
    const { pricingModel, pricing } = event;
    if (pricingModel === 'free') return <p style={priceTextStyle}>Free — registration required</p>;
    if (pricingModel === 'flat') return <p style={priceTextStyle}>${((pricing?.price || 0) / 100).toFixed(2)} per person</p>;
    if (pricingModel === 'early_bird') {
      const now = new Date();
      const deadline = pricing?.earlyBirdDeadline ? new Date(pricing.earlyBirdDeadline + 'T23:59:59') : null;
      const isEarlyBird = deadline && now <= deadline;
      return (
        <div>
          {isEarlyBird && (
            <p style={{ ...priceTextStyle, color: '#16A34A', marginBottom: 4 }}>
              ${((pricing.earlyBirdPrice || 0) / 100).toFixed(2)} early bird (until {formatDateShort(pricing.earlyBirdDeadline).replace(/,.*$/, '')})
            </p>
          )}
          <p style={{ ...priceTextStyle, ...(isEarlyBird ? { fontSize: 14, color: '#6B7280' } : {}) }}>
            ${((pricing?.price || 0) / 100).toFixed(2)} {isEarlyBird ? 'regular price' : 'per person'}
          </p>
        </div>
      );
    }
    if (pricingModel === 'deposit') {
      const remainderDate = event.schedule?.date
        ? (() => { const d = new Date(event.schedule.date + 'T12:00:00'); d.setDate(d.getDate() - (pricing?.remainderDueDays || 7)); return formatDateShort(d.toISOString().split('T')[0]); })()
        : null;
      return (
        <div>
          <p style={priceTextStyle}>${((pricing?.depositAmount || 0) / 100).toFixed(2)} deposit to reserve your spot</p>
          <p style={{ fontSize: 14, color: '#6B7280', fontFamily: 'Montserrat, sans-serif', marginTop: 4 }}>
            ${((pricing?.totalPrice || 0) / 100).toFixed(2)} total {remainderDate ? `· Remaining balance due ${remainderDate}` : ''}
          </p>
        </div>
      );
    }
    return null;
  };

  // Register button
  const renderRegisterButton = () => {
    if (past || cancelled) return null;

    // Already registered
    if (isRegistered) {
      return (
        <div style={{ textAlign: 'center', margin: '28px 0' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 48px', borderRadius: 8, background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
            <CheckCircle size={18} color="#16A34A" />
            <span style={{ fontWeight: 700, fontSize: 16, color: '#15803D', fontFamily: 'Montserrat, sans-serif' }}>You're registered</span>
          </div>
          <div style={{ marginTop: 8 }}>
            <span
              role="button"
              tabIndex={0}
              onClick={handleCancelRegistration}
              onKeyDown={(e) => e.key === 'Enter' && handleCancelRegistration()}
              style={{ fontSize: 13, color: '#DC2626', fontFamily: 'Montserrat, sans-serif', cursor: 'pointer', userSelect: 'none', opacity: cancelling ? 0.5 : 1 }}
            >
              {cancelling ? 'Cancelling...' : 'Cancel registration'}
            </span>
          </div>
        </div>
      );
    }

    return (
      <div style={{ textAlign: 'center', margin: '28px 0' }}>
        <span
          role="button"
          tabIndex={0}
          onClick={(!soldOut && !registrationClosed && !registering) ? handleRegister : undefined}
          onKeyDown={(e) => e.key === 'Enter' && !soldOut && !registrationClosed && !registering && handleRegister()}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '14px 48px', borderRadius: 8,
            fontWeight: 700, fontSize: 16, fontFamily: 'Montserrat, sans-serif',
            transition: 'opacity 0.15s', userSelect: 'none', minWidth: 240,
            ...(soldOut || registrationClosed || registering
              ? { background: '#E5E7EB', color: '#9CA3AF', cursor: 'default' }
              : { background: '#bb9749', color: '#FFFFFF', cursor: 'pointer' }
            ),
          }}
        >
          {registering && <Loader2 size={18} className="animate-spin" />}
          {soldOut ? 'Sold Out' : registrationClosed ? 'Registration Closed' : registering ? 'Registering...' : 'Register for this event →'}
        </span>
        {regError && (
          <p style={{ color: '#DC2626', fontSize: 13, fontFamily: 'Montserrat, sans-serif', marginTop: 8 }}>{regError}</p>
        )}
      </div>
    );
  };

  // SEO
  const metaTitle = `${event.title} — ${practitionerName}`;
  const metaDesc = stripHtml(event.description || '').slice(0, 160);
  const metaImage = event.coverImage || practitionerPhoto || DEFAULT_OG_IMAGE;
  const metaUrl = `${SITE_BASE_URL}/site/${practitionerSlug}/events/${eventSlug}`;

  const eventSchema = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": event.title,
    "description": stripHtml(event.description || ''),
    "startDate": event.schedule?.date || event.schedule?.startDate || '',
    "endDate": event.schedule?.endDate || null,
    "eventAttendanceMode": event.format === 'virtual'
      ? "https://schema.org/OnlineEventAttendanceMode"
      : event.format === 'in_person'
      ? "https://schema.org/OfflineEventAttendanceMode"
      : "https://schema.org/MixedEventAttendanceMode",
    "location": event.format === 'virtual'
      ? { "@type": "VirtualLocation", "url": metaUrl }
      : event.venue?.name
      ? { "@type": "Place", "name": event.venue.name, "address": event.venue.address || '' }
      : undefined,
    "organizer": { "@type": "Person", "name": practitionerName, "url": `${SITE_BASE_URL}/site/${practitionerSlug}` },
    "offers": {
      "@type": "Offer",
      "price": event.pricingModel === 'free' ? "0" : ((event.pricing?.price || 0) / 100).toFixed(2),
      "priceCurrency": "USD",
      "availability": spotsLeft > 0 ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
    },
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle} | Divine Frameworks</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content={metaImage} />
        <meta property="og:url" content={metaUrl} />
        <meta property="og:type" content="event" />
        <meta property="og:site_name" content="Divine Frameworks" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={metaImage} />
        <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
      </Helmet>

      <div style={{ minHeight: '100vh', background: '#F9FAFB', fontFamily: 'Montserrat, sans-serif' }}>
        {cancelled && <Banner color="#DC2626" bgColor="#FEF2F2">This event has been cancelled</Banner>}
        {past && !cancelled && <Banner color="#6B7280" bgColor="#F3F4F6">This event has ended</Banner>}

        {/* Cover image */}
        <div style={{
          height: cancelled ? 0 : 240,
          background: event.coverImage ? `url(${event.coverImage}) center/cover no-repeat` : 'linear-gradient(135deg, #7c3aed, #a78bfa)',
          position: 'relative',
        }}>
          {!cancelled && (
            <div style={{ position: 'absolute', top: 16, left: 16 }}>
              <a href={`/site/${practitionerSlug}`} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
                background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(4px)',
                color: '#374151', fontSize: 13, fontWeight: 600, textDecoration: 'none', fontFamily: 'Montserrat, sans-serif',
              }}>
                <ArrowLeft size={14} /> Back to site
              </a>
            </div>
          )}
        </div>

        {!cancelled && (
          <div style={{ maxWidth: 700, margin: '0 auto', padding: '32px 16px 80px' }}>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#111827', margin: '0 0 6px', lineHeight: 1.3 }}>{event.title}</h1>
            <p style={{ fontSize: 15, color: '#6B7280', marginBottom: 28 }}>with {practitionerName}</p>

            {/* Registration success card */}
            {(isRegistered || justRegistered) && (
              <RegistrationSuccess event={event} registration={registration} practitionerSlug={practitionerSlug} />
            )}

            {/* Details */}
            <InfoCard title="Details">
              {event.eventType === 'single' && event.schedule?.date && (
                <DetailRow icon={Calendar}>{formatDateLong(event.schedule.date)}</DetailRow>
              )}
              {event.eventType === 'series' && event.schedule?.sessionDates?.[0] && (
                <DetailRow icon={Calendar}>Starting {formatDateLong(event.schedule.sessionDates[0])} · {event.schedule.sessionDates.length} sessions</DetailRow>
              )}
              {event.eventType === 'multi_day' && event.schedule?.date && (
                <DetailRow icon={Calendar}>{formatDateLong(event.schedule.date)} — {formatDateLong(event.schedule.endDate)}</DetailRow>
              )}
              {event.eventType === 'single' && event.schedule?.startTime && (
                <DetailRow icon={Clock}>{formatTime12(event.schedule.startTime)} — {formatTime12(event.schedule.endTime)} ({getTimezoneLabel(event.timezone)})</DetailRow>
              )}
              {event.eventType === 'series' && event.schedule?.recurringTime && (
                <DetailRow icon={Clock}>{formatTime12(event.schedule.recurringTime)} — {formatTime12(minutesToTime(parseTimeToMinutes(event.schedule.recurringTime) + (event.schedule.sessionDuration || 0)))} ({getTimezoneLabel(event.timezone)})</DetailRow>
              )}
              <DetailRow icon={event.format === 'in_person' ? MapPin : Monitor}>
                {event.format === 'virtual' && (isRegistered ? (event.zoomJoinUrl || event.manualMeetingLink || 'Virtual — link will be shared before the event') : 'Virtual — link provided after registration')}
                {event.format === 'in_person' && `In-Person${event.venue?.name ? ` at ${event.venue.name}` : ''}`}
                {event.format === 'hybrid' && 'Hybrid (Virtual + In-Person)'}
              </DetailRow>
              <DetailRow icon={Users}>
                {soldOut ? <span style={{ color: '#D97706', fontWeight: 600 }}>Sold Out</span> : `${spotsLeft} of ${event.maxAttendees} spots available`}
              </DetailRow>
            </InfoCard>

            {/* Pricing */}
            <InfoCard title="Pricing">{renderPricing()}</InfoCard>

            {/* Register button */}
            {renderRegisterButton()}

            {/* Description */}
            {event.description && (
              <InfoCard title="About This Event">
                <div className="tiptap" style={{ fontSize: 15, color: '#374151', lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(event.description) }} />
              </InfoCard>
            )}

            {/* Series schedule */}
            {event.eventType === 'series' && event.schedule?.sessionDates?.length > 0 && (
              <InfoCard title={`${event.schedule.sessionDates.length}-Session Series`}>
                <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 12 }}>Registration includes all sessions.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {event.schedule.sessionDates.map((dateStr, i) => {
                    const endTime = event.schedule.recurringTime && event.schedule.sessionDuration
                      ? minutesToTime(parseTimeToMinutes(event.schedule.recurringTime) + event.schedule.sessionDuration) : null;
                    return (
                      <div key={dateStr} style={{ display: 'flex', gap: 8, fontSize: 14, color: '#374151' }}>
                        <span style={{ fontWeight: 600, minWidth: 80 }}>Session {i + 1}:</span>
                        <span>{formatDateShort(dateStr)}{event.schedule.recurringTime && `, ${formatTime12(event.schedule.recurringTime)}`}{endTime && ` — ${formatTime12(endTime)}`}</span>
                      </div>
                    );
                  })}
                </div>
              </InfoCard>
            )}

            {/* Multi-day schedule */}
            {event.eventType === 'multi_day' && event.schedule?.dailySchedule?.length > 0 && (
              <InfoCard title="Schedule">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {event.schedule.dailySchedule.map((day) => (
                    <div key={day.date} style={{ display: 'flex', gap: 12, fontSize: 14, color: '#374151', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600, minWidth: 160 }}>{formatDateShort(day.date)}</span>
                      <span>{formatTime12(day.startTime)} — {formatTime12(day.endTime)} {getTimezoneAbbr(event.timezone)}</span>
                    </div>
                  ))}
                </div>
              </InfoCard>
            )}

            {/* Venue */}
            {(event.format === 'in_person' || event.format === 'hybrid') && event.venue && (
              <InfoCard title="Location">
                <div style={{ fontSize: 15, color: '#374151' }}>
                  {event.venue.name && <p style={{ fontWeight: 600, marginBottom: 4 }}>{event.venue.name}</p>}
                  {event.venue.address && <p style={{ marginBottom: 8 }}>{event.venue.address}</p>}
                  {event.venue.directions && <p style={{ fontSize: 14, color: '#6B7280', fontStyle: 'italic' }}>{event.venue.directions}</p>}
                </div>
              </InfoCard>
            )}

            {/* Practitioner */}
            <InfoCard title="About the Practitioner">
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                {practitionerPhoto ? (
                  <img src={practitionerPhoto} alt={practitionerName} style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 56, height: 56, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg, #7c3aed, #22d3ee)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFF', fontSize: 20, fontWeight: 700 }}>
                    {(practitionerName || '?')[0].toUpperCase()}
                  </div>
                )}
                <div>
                  <p style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>{practitionerName}</p>
                  {practitionerBio && <p style={{ fontSize: 13, color: '#6B7280', margin: '2px 0 0' }}>{practitionerBio}</p>}
                  <a href={`/site/${practitionerSlug}`} style={{ fontSize: 13, color: '#7c3aed', fontWeight: 600, textDecoration: 'none', marginTop: 4, display: 'inline-block' }}>View full profile →</a>
                </div>
              </div>
            </InfoCard>

            {/* Bottom register button */}
            {renderRegisterButton()}
          </div>
        )}
      </div>
    </>
  );
};

const priceTextStyle = {
  fontSize: 18, fontWeight: 700, color: '#111827', fontFamily: 'Montserrat, sans-serif', margin: 0,
};

export default EventDetailPage;
